import React, {
  useCallback,
  useEffect,
  useReducer,
  useState,
  ReactElement
} from "react";
import { TabPane } from "../Tabs/Tabs";

import { useUserSettings } from "components/Site/Contexts/UserSettings";
import { SearchBar, SearchInput, StyledCard, StyledTabs } from "./styles";
import SearchDoubleIcon from "icons/SearchDoubleIcon";

export const TabSelectMenuPane = TabPane;

interface TabSelectMenuState {
  resizeHeight: boolean;
  search: string;
  activeSearchItemIndex: number;
}

enum TabSelectActionType {
  search = "search",
  resize = "resize",
  upArrow = "upArrow",
  leftArrow = "leftArrow",
  downArrow = "downArrow",
  rightArrow = "rightArrow",
  reset = "reset"
}

interface TabSelectAction {
  type: TabSelectActionType;
  payload?: any; // TODO: research best way to type redux payloads
}

const defaultTabSelectMenuState: TabSelectMenuState = {
  resizeHeight: false,
  search: "",
  activeSearchItemIndex: 0
};

interface TabSelectMenuMenuItems {
  key: string;
  onSelect: () => {};
  searchValues: string[];
  render: (props: any) => ReactElement;
}

interface TabSelectMenuMenu {
  tab: ReactElement;
  tabPaneComponent: (props: any) => ReactElement;
  key: string;
  activeMenuItemIndex: number;
  menuItems: TabSelectMenuMenuItems[];
}

function tabSelectMenuReducer(
  state: TabSelectMenuState,
  action: TabSelectAction
) {
  switch (action.type) {
    case TabSelectActionType.search:
      const search = action.payload;
      return {
        ...state,
        resizeHeight: !state.resizeHeight,
        search,
        activeSearchItemIndex: 0 // search === "" ? null : 0
      };
    case TabSelectActionType.resize:
      return {
        ...state,
        resizeHeight: !state.resizeHeight
      };
    case TabSelectActionType.upArrow:
    case TabSelectActionType.leftArrow:
      return {
        ...state,
        activeSearchItemIndex: state.activeSearchItemIndex - 1
      };
    case TabSelectActionType.downArrow:
    case TabSelectActionType.rightArrow:
      return {
        ...state,
        activeSearchItemIndex: state.activeSearchItemIndex + 1
      };
    case TabSelectActionType.reset:
      return {
        ...defaultTabSelectMenuState,
        resizeHeight: !state.resizeHeight,
        activeSearchItemIndex: action.payload.activeSearchItemIndex
      };
    default:
      throw new Error("invalid action type for tabSelectMenuReducer");
  }
}

const matchRegexOperatorsRegex = /[|\\{}()[\]^$+*?.-]/g;

function filterSearchValues(
  menuItems: TabSelectMenuMenuItems[],
  search: string
) {
  const escapedSearch = search.replace(matchRegexOperatorsRegex, "\\$&");
  const searchRegExp = new RegExp(`^${escapedSearch}|\\s${escapedSearch}`, "i");
  return menuItems.filter(({ searchValues }) =>
    searchValues.some(value => searchRegExp.test(value))
  );
}

// allows negative numbers to wrap around the back of the list
const findActiveRowIndex = (
  activeSearchItemIndex: number,
  menuItemLength: number
) =>
  ((activeSearchItemIndex % menuItemLength) + menuItemLength) % menuItemLength;

export interface TabSelectMenuProps {
  menus: TabSelectMenuMenu[];
  activeMenuKey: string;
  onTabClick: (key: string) => void;
}

export const TabSelectMenu: React.FC<TabSelectMenuProps> = ({
  menus = [],
  activeMenuKey,
  onTabClick,
  ...props
}) => {
  const { darkMode } = useUserSettings();

  const [activeTabKey, setActiveTabKey] = useState(
    activeMenuKey || menus[0].key || ""
  );
  const [state, dispatch] = useReducer(tabSelectMenuReducer, {
    ...defaultTabSelectMenuState,
    activeSearchItemIndex:
      (menus.find(({ key }) => key === activeTabKey) || {})
        .activeMenuItemIndex || 0
  });

  const totalMenuItems = menus
    .map(menu => menu.menuItems.length)
    .reduce((a, b) => a + b);
  useEffect(() => {
    dispatch({ type: TabSelectActionType.resize });
  }, [activeTabKey, totalMenuItems]);

  useEffect(() => {
    activeMenuKey && setActiveTabKey(activeMenuKey);
  }, [activeMenuKey]);

  const setFocus = useCallback(
    node => activeTabKey && node !== null && node.focus(),
    [activeTabKey]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: TabSelectActionType.search, payload: e.target.value });

  const handleTabClick = (newActiveTabKey: string) => {
    if (onTabClick) {
      onTabClick(newActiveTabKey);
    } else {
      setActiveTabKey(newActiveTabKey);
    }

    dispatch({
      type: TabSelectActionType.reset,
      payload: {
        activeSearchItemIndex:
          (menus.find(({ key }) => key === newActiveTabKey) || {})
            .activeMenuItemIndex || 0
      }
    });
  };

  // filter our menu items of current tab if there is a search value
  menus = menus.map((menu, index) => ({
    ...menu,
    menuItems:
      menu.key === activeTabKey && state.search !== ""
        ? filterSearchValues(menu.menuItems, state.search)
        : menu.menuItems
  }));

  const menuTabPanes = menus.map(
    ({
      tab,
      key,
      menuItems,
      activeMenuItemIndex,
      tabPaneComponent: TabPaneComponent
    }) => {
      return (
        <TabPane tab={tab} key={key}>
          <TabPaneComponent
            menuItems={menuItems.map(menuItem => ({
              ...menuItem,
              onSelect: () => {
                dispatch({ type: TabSelectActionType.search, payload: "" });
                menuItem.onSelect();
              }
            }))}
            defaultActiveRowIndex={activeMenuItemIndex}
            activeRowIndex={findActiveRowIndex(
              state.activeSearchItemIndex,
              menuItems.length
            )}
            searchValue={state.search}
          />
        </TabPane>
      );
    }
  );

  const handleNavigation = (e: React.KeyboardEvent) => {
    if (e.keyCode === 37)
      return dispatch({ type: TabSelectActionType.leftArrow });
    if (e.keyCode === 39)
      return dispatch({ type: TabSelectActionType.rightArrow });
    if (e.keyCode === 38) {
      e.preventDefault(); // keeps cursor from moving to start of inpupt
      return dispatch({ type: TabSelectActionType.upArrow });
    }
    if (e.keyCode === 40)
      return dispatch({ type: TabSelectActionType.downArrow });
    if (e.keyCode === 13) {
      const currentMenuItems =
        (menus.find(menu => menu.key === activeTabKey) || {}).menuItems || [];
      currentMenuItems[
        state.activeSearchItemIndex % currentMenuItems.length
      ].onSelect();
      dispatch({ type: TabSelectActionType.search, payload: "" });
    }
  };

  return (
    <StyledCard {...props}>
      <StyledTabs
        onTabClick={handleTabClick}
        activeKey={activeTabKey}
        resizeHeight={state.resizeHeight}
        prependTabContent={
          <SearchBar>
            <SearchDoubleIcon size={24} color={darkMode ? "white" : "black"} />
            <SearchInput
              ref={setFocus}
              type="text"
              value={state.search}
              onChange={handleSearch}
              onKeyDown={handleNavigation}
            />
          </SearchBar>
        }
      >
        {menuTabPanes}
      </StyledTabs>
    </StyledCard>
  );
};
